<template>
  <div class="form-container">
    <div
      :style="{'width': formWidth +'px'}"
    >
      <el-form
        :label-width="labelWidth"
        :model="fM"
        :rules="fR"
        ref="cFormRef"
      >
        <!-- 主表单字段 -->
        <el-form-item
          v-for="(item, index) in fS"
          :key="index"
          :label="item.label"
          :prop="item.prop"
        >
          <!-- 级联选择器 -->
          <el-cascader
            v-if="item.component === 'el-cascader'"
            :id="item.prop"
            :placeholder="item.placeholder"
            :options="item.options"
            v-model="fM[item.prop]"
            style="width:100%"
          />

          <!-- 动态表单组 -->
          <div
            v-else-if="item.component === 'custom-groups'"
            class="dynamic-groups"
          >
            <!-- 动态组列表 -->
            <el-row
              v-for="(group, groupIndex) in formGroups"
              :key="group.id"
              :gutter="20"
              class="group-row"
            >
              <el-col :span="16">
                <el-card class="group-card">
                  <DynamicGroupForm
                    :labelWidth="groupLabelWidth"
                    :model-value="group.model"
                    :group-index="groupIndex"
                    :fields="groupFields"
                    :rules="groupFormRules"
                    :readonly="readonly"
                    @set-form-ref="setGroupFormRef"
                    @update:modelValue="newModel => updateGroupModel(groupIndex, newModel)"
                  />
                </el-card>
              </el-col>
              <el-col
                :span="4"
                class="button-container"
              >
                <el-button
                  v-if="!readonly && (groupIndex !== 0 || !defaultGroup)"
                  type="danger"
                  circle
                  icon="el-icon-delete"
                  @click="removeGroup(groupIndex)"
                />
              </el-col>
            </el-row>

            <!-- 添加按钮 -->
            <el-row
              v-if="!readonly && permitAddGroup"
              :gutter="20"
            >
              <el-col
                :span="16"
                style="display: flex; margin-top: 12px"
              >
                <el-button
                  type="success"
                  icon="el-icon-circle-plus-outline"
                  style="flex: 1"
                  @click="addGroup"
                />
              </el-col>
            </el-row>
          </div>
          <!-- radio 选项 -->
          <template v-else-if="item.component === 'el-radio-group'">
            <el-radio-group
              :is="item.component"
              :id="item.prop"
              :type="item.type"
              v-model="fM[item.prop]"
              :disabled="readonly || item.readonly"
            >
              <el-radio
                v-for="(option, optionIndex) in item.options"
                :key="optionIndex"
                :label="option.value"
              >
                {{ option.label }}
              </el-radio>
            </el-radio-group>
          </template>
          <!-- 普通表单字段 -->
          <component
            v-else
            :is="item.component"
            :id="item.prop"
            :type="item.type"
            :placeholder="item.placeholder"
            :min="item.min"
            :max="item.max"
            :filterable="item.filterable"
            :disabled="item.disabled"
            :multiple="item.multiple"
            :readonly="readonly || item.readonly"
            :remote="item.remote"
            :remote-method="item.remoteMethod"
            :loading="item.loading"
            v-model="fM[item.prop]"
            clearable
            :show-password="item.showPassword"
            :style="{width: item.component === 'custom-groups' ? '100%' : '50%'}"
          >
            <!-- Select 选项 -->
            <template v-if="item.component === 'el-select'">
              <el-option
                v-for="option in item.options"
                :key="option.value"
                :label="option.label"
                :value="option.value"
              />
            </template>
          </component>
        </el-form-item>

        <slot :row="fM" />

        <!-- 操作按钮 -->
        <el-form-item v-if="!readonly">
          <el-button
            type="primary"
            @click="submitEmit"
          >
            {{ t(submitButton) }}
          </el-button>
          <el-button @click="resetEmit">
            {{ t(resetButton) }}
          </el-button>
        </el-form-item>
      </el-form>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, PropType } from 'vue'
import { useI18n } from 'vue-i18n'
import DynamicGroupForm from '@/components/form/common/DynamicGroupForm.vue'
import { ElForm } from 'element-plus'
import { GroupInstance, GroupField } from '@/components/form/common/DynamicFormModel'

export default defineComponent({

  components: {
    DynamicGroupForm
  },

  props: {
    formModel: {
      type: Object,
      default: () => {
        return null
      }
    },
    formStruct: {
      type: Array,
      default: () => [] as any
    },
    formRules: {
      type: Object,
      default: () => {
        return null
      }
    },
    groupsModel: {
      type: Array as PropType<Record<string, any>[]>,
      default: () => []
    },
    groupsStruct: {
      type: Array as () => GroupField[],
      default: () => [] as any
    },
    groupsRules: {
      type: Object,
      default: () => {
        return null
      }
    },
    submitButton: {
      type: String,
      default: 'common.submit'
    },
    resetButton: {
      type: String,
      default: 'common.reset'
    },
    formWidth: {
      type: Number,
      default: 850
    },
    labelWidth: {
      type: String,
      default: '150px'
    },
    groupLabelWidth: {
      type: String,
      default: '100px'
    },
    readonly: {
      type: Boolean,
      default: false
    },
    defaultGroup: {
      type: Boolean,
      default: false
    },
    permitAddGroup: {
      type: Boolean,
      default: true
    }
  },

  emits: ['submit', 'reset', 'groups-change'],

  setup(props, context) {
    const { t } = useI18n()
    const cFormRef = ref(ElForm)
    const fM = ref(props.formModel || {})
    const fS = ref(props.formStruct)
    const fR = ref(props.formRules)

    // 动态组相关状态
    let formGroupId = 0

    // 从父组件获取字段配置
    const groupFields = computed<GroupField[]>(() => {
      return props.groupsStruct || []
    })
    const groupFormRules = computed(() => {
      return props.groupsRules || {}
    })
    // 存放动态组的 form refs
    const groupFormRefs = ref<any[]>([])

    // 子组件调用时执行
    const setGroupFormRef = (groupIndex: number, el: any) => {
      if (el) {
        groupFormRefs.value[groupIndex] = el
      } else {
        // 防止 v-for 删除后还保留旧 ref
        groupFormRefs.value.splice(groupIndex, 1)
      }
    }

    // 创建新的信息组
    const createGroup = (model?: Record<string, any>): GroupInstance => {
      formGroupId++

      const defaultData: Record<string, any> = {}

      // 确保遍历 groupFields 并初始化所有属性
      groupFields.value.forEach(field => {
        defaultData[field.prop] = model?.[field.prop] ?? ''
      })

      return {
        id: formGroupId,
        model: defaultData
      }
    }
    const formGroupsRefs = ref<any[]>([])
    const formGroups = ref<GroupInstance[]>(
      props.groupsModel && props.groupsModel.length > 0
        ? props.groupsModel.map(model => createGroup(model))
        : props.defaultGroup
          ? [createGroup()]
          : []
    )

    const addGroup = () => {
      const newGroup = createGroup()
      formGroups.value.push(newGroup)
      context.emit('groups-change', formGroups.value.map(group => group.model))
    }

    const removeGroup = (index: number) => {
      if (!props.defaultGroup || formGroups.value.length > 1) {
        formGroups.value.splice(index, 1)
        formGroupsRefs.value.splice(index, 1)
        context.emit('groups-change', formGroups.value.map(group => group.model))
      }
    }

    // const submitEmit = () => {
    //   (cFormRef.value as typeof ElForm).validate((valid: any) => {
    //     if (valid) {
    //       const submitData = {
    //         ...fM.value,
    //         model: formGroups.value.map(group => group.model)
    //       }
    //       context.emit('submit', submitData)
    //       console.log('提交数据:', JSON.stringify(submitData))
    //     }
    //   })
    // }
    const submitEmit = async() => {
      // 校验主表单
      const mainValid = await cFormRef.value?.validate().catch(() => false)
      // if (!mainValid) {
      //   console.warn('主表单校验失败')
      //   return
      // }

      // 校验所有动态子表单
      const groupValid = await Promise.all(
        groupFormRefs.value.map(form =>
          form?.validate().catch(() => false)
        )
      )
      if (groupValid.includes(false) || !mainValid) {
        return
      }

      // 所有表单都通过了
      const submitData = {
        ...fM.value,
        model: formGroups.value.map(group => group.model)
      }
      context.emit('submit', submitData)
    }

    const resetEmit = () => {
      (cFormRef.value as typeof ElForm).resetFields()
      formGroups.value = []
      formGroupsRefs.value = []

      if (props.defaultGroup) {
        addGroup()
      }
      // 重置动态表单组到初始状态
      formGroups.value = props.groupsModel && props.groupsModel.length > 0
        ? props.groupsModel.map(model => createGroup(model))
        : props.defaultGroup
          ? [createGroup()]
          : []
      context.emit('reset')
    }

    const updateGroupModel = (groupIndex: number, newModel: Record<string, any>) => {
      if (formGroups.value[groupIndex]) {
        formGroups.value[groupIndex].model = newModel
      }
    }

    // 初始化
    const initGroups = () => {
      if (props.defaultGroup && props.groupsModel.length < 1) {
        addGroup()
      }
    }

    initGroups()

    return {
      t,
      fM,
      fS,
      fR,
      cFormRef,
      formGroups,
      groupFields,
      groupFormRules,
      updateGroupModel,
      addGroup,
      removeGroup,
      setGroupFormRef,
      submitEmit,
      resetEmit
    }
  }
})
</script>

<style scoped>
.form-container {
  width: 100%;
  padding: 15px;
}

.dynamic-groups {
  width: 100%;
}

.group-row {
  margin-top: 10px;
}

.group-card {
  border: 1px solid #e4e7ed;
}

.button-container {
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
